import { z } from 'zod';
import { API_CONFIG } from '../config/api';
import { apiClient } from '../lib/apiClient';
import { authUserSchema, envelopeSchema } from '../lib/schemas';
import { logger } from './logger';
import { normalizeRole } from './roles';

export type AuthUser = z.infer<typeof authUserSchema>;

export class EmployeeNotFoundError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'EmployeeNotFoundError';
  }
}

export const getCurrentUser = async (): Promise<AuthUser | null> => {
  try {
    const apiUrl = `${API_CONFIG.apiVersion}/auth/me`;

    try {
      const data = await apiClient.get(apiUrl, {
        validate: envelopeSchema('user', authUserSchema),
        deduplicate: true,
        context: 'getCurrentUser',
      });

      if (!data.user) {
        return null;
      }

      return {
        ...data.user,
        role: normalizeRole(data.user.role),
      };
    } catch (apiError: unknown) {
      if (apiError instanceof Error && 'status' in apiError) {
        const status = (apiError as { status: number }).status;

        if (status === 401) {
          logger.debug('No authenticated session found (401)');
          return null;
        }

        if (status === 404) {
          logger.debug('Auth endpoint not found (404) - authentication may not be configured');
          return null;
        }

        if (status === 403) {
          const errorData = (apiError as { data?: { error?: { code?: string; message?: string } } })
            .data;
          if (errorData?.error?.code === 'employee_not_found') {
            throw new EmployeeNotFoundError(
              errorData.error.message || 'Employee not found in database'
            );
          }
          logger.warn(
            '[getCurrentUser] 403 received but code is not employee_not_found:',
            errorData?.error?.code
          );
          return null;
        }

        logger.warn(`Auth check failed with status ${status}`);
        return null;
      }

      throw apiError;
    }
  } catch (error) {
    if (error instanceof EmployeeNotFoundError) {
      throw error;
    }
    logger.error('Failed to get current user:', error);
    return null;
  }
};

export const logout = async (): Promise<void> => {
  try {
    const apiUrl = `${API_CONFIG.apiVersion}/auth/logout`;
    await apiClient.post(apiUrl);
  } catch (error) {
    logger.error('Failed to logout:', error);
  }
};
